/* eslint-disable @typescript-eslint/no-unused-vars */
import { isDefined } from "@/lib/utils";
import { Row, RowData } from "@tanstack/react-table";
import {
  functionalUpdate,
  makeStateUpdater,
  OnChangeFn,
  PaginationState,
  Table,
  TableFeature,
  Updater,
} from "@tanstack/table-core";

// define types for our new feature's custom state
export type PositionStateBase = {
  id?: string;
  index?: number;
};
export type PositionState = PositionStateBase & {
  lastValid?: PositionStateBase;
};
export interface PositionTableState {
  position: PositionState;
}

// define types for our new feature's table options
export interface PositionOptions {
  enablePosition?: boolean;
  onPositionChange?: OnChangeFn<PositionState>;
}

export type ArgUpdater<T, Args extends any[]> =
  | T
  | ((s: T, ...args: Args) => T);
export function functionalArgUpdate<T, Args extends any[]>(
  updater: ArgUpdater<T, Args>,
  input: T,
  ...args: Args
): T {
  return typeof updater === "function"
    ? (updater as any)(input, ...args)
    : updater;
}

// Define types for our new feature's table APIs
export interface PositionInstance<TData> {
  setPosition: (
    updater: Updater<PositionState>,
    options?: { searchAll?: boolean; navigate?: boolean },
  ) => void;
  setPositionId: (
    updater: ArgUpdater<PositionState["id"], [PositionState]>,
    searchAll?: boolean,
  ) => void;
  setPositionIndex: (
    updater: ArgUpdater<PositionState["index"], [PositionState]>,
    navigate?: boolean,
  ) => void;
  getPositionRow: (index?: number) => Row<TData> | undefined;
  getCanNextPosition: () => boolean;
  getCanPreviousPosition: () => boolean;
  shouldContainPosition: (position?: PositionState) => boolean;
}

export interface PositionRow {
  getPositionIndex: () => number;
  getIsPosition: () => boolean;
  setIsPosition: (updater?: Updater<boolean>) => void;
}

// Use declaration merging to add our new feature APIs and state types to TanStack Table's existing types.
declare module "@tanstack/react-table" {
  //merge our new feature's state with the existing table state
  interface TableState extends PositionTableState {}
  //merge our new feature's options with the existing table options
  interface TableOptionsResolved<TData extends RowData>
    extends PositionOptions {}
  //merge our new feature's instance APIs with the existing table instance APIs
  interface Table<TData extends RowData> extends PositionInstance<TData> {}
  // if you need to add cell instance APIs...
  // interface Cell<TData extends RowData, TValue> extends PositionCell
  // if you need to add row instance APIs...
  interface Row<TData extends RowData> extends PositionRow {}
  // if you need to add column instance APIs...
  // interface Column<TData extends RowData, TValue> extends PositionColumn
  // if you need to add header instance APIs...
  // interface Header<TData extends RowData, TValue> extends PositionHeader

  // Note: declaration merging on `ColumnDef` is not possible because it is a type, not an interface.
  // But you can still use declaration merging on `ColumnDef.meta`
}

// end of TS setup!

const getStart = (pagination: PaginationState) =>
  pagination.pageSize * pagination.pageIndex;

// Here is all of the actual javascript code for our new feature
export const PositionFeature: TableFeature<any> = {
  // define the new feature's initial state
  getInitialState: (state): PositionTableState => {
    return {
      position: {},
      ...state,
    };
  },

  // define the new feature's default options
  getDefaultOptions: <TData extends RowData>(
    table: Table<TData>,
  ): PositionOptions => {
    return {
      enablePosition: true,
      onPositionChange: makeStateUpdater("position", table),
    } as PositionOptions;
  },
  // if you need to add a default column definition...
  // getDefaultColumnDef: <TData extends RowData>(): Partial<ColumnDef<TData>> => {
  //   return { meta: {} } //use meta instead of directly adding to the columnDef to avoid typescript stuff that's hard to workaround
  // },

  // define the new feature's table instance methods
  createTable: <TData extends RowData>(table: Table<TData>): void => {
    table.setPosition = (updater, options) => {
      return table.options.onPositionChange?.((old) => {
        const next = functionalUpdate(updater, old);
        if (next.id !== undefined && next.index === undefined) {
          try {
            next.index = table.getRow(next.id, options?.searchAll)?.index;
          } catch (ex) {
            // ignore, index not found in pagination
          }
        } else if (next.index !== undefined && next.id === undefined) {
          const index = next.index;
          const pagination = table.getState().pagination;
          const start = pagination.pageIndex * pagination.pageSize;
          const end = start + pagination.pageSize;
          if (index < start || index >= end) {
            if (options?.navigate) {
              table.setPagination((pagination) => {
                if (pagination.pageSize <= 0) {
                  return pagination;
                }

                // const pageIndex =
                //   Math.max(1, Math.ceil(index / pagination.pageSize)) - 1;

                const pageIndex = Math.floor(index / pagination.pageSize);
                return {
                  ...pagination,
                  pageIndex,
                };
              });
            }
          } else {
            next.id = table.getPaginationRowModel().rows.at(index - start)?.id;
          }
        }
        return next;
      });
    };
    table.setPositionId = (updater, searchAll?: boolean) => {
      table.setPosition(
        (prev) => {
          const old = prev?.id;
          const next = functionalArgUpdate(updater, old, prev);
          return next == undefined ? {} : { id: next };
        },
        { searchAll },
      );
    };
    table.setPositionIndex = (updater, navigate?: boolean) => {
      table.setPosition(
        (prev) => {
          const old = prev?.index;
          const next = functionalArgUpdate(updater, old, prev);
          return next == undefined ? {} : { index: next };
        },
        { navigate },
      );
    };
    table.getCanNextPosition = () => {
      const state = table.getState();
      const index = state.position.index ?? state?.position?.lastValid?.index;
      if (index === undefined) return false;
      const rowCount = table.getRowCount();
      return index < rowCount - 1;
    };
    table.getCanPreviousPosition = () => {
      const state = table.getState();
      const index = state.position.index ?? state?.position?.lastValid?.index;
      if (index === undefined) return false;
      return index > 0;
    };
    table.getPositionRow = (_index?: number) => {
      const index = _index ?? table.getState().position?.index;
      if (!isDefined(index)) return;
      const pagination = table.getState().pagination;
      const start = pagination.pageIndex * pagination.pageSize;
      return table.getPaginationRowModel().rows.at(index - start);
    };
    table.shouldContainPosition = (_position?: PositionState) => {
      const position = _position ?? table.getState().position;
      const index = position.index;
      if (!isDefined(index)) return isDefined(position.id);
      const pagination = table.getState().pagination;
      const start = pagination.pageIndex * pagination.pageSize;
      const end = start + pagination.pageSize;
      return !(index < start || index >= end);
    };
  },

  // if you need to add row instance APIs...
  createRow: <TData extends RowData>(
    row: Row<TData>,
    table: Table<TData>,
  ): void => {
    row.getPositionIndex = () =>
      row.index + getStart(table.getState().pagination);
    row.getIsPosition = () => {
      const position = table.getState().position;
      return (
        (position.index !== undefined &&
          row.getPositionIndex() == position.index) ||
        (row.id !== undefined &&
          position.id !== undefined &&
          row.id == position.id)
      );
    };
    row.setIsPosition = (updater = (t) => !t) => {
      table.setPosition((prev) => {
        const positionIndex = row.getPositionIndex();
        const old =
          (prev.index !== undefined && positionIndex === prev.index) ||
          (row.id !== undefined && prev.id !== undefined && row.id === prev.id);
        const next = functionalUpdate(updater, old);
        return next ? { id: row.id, index: positionIndex } : {};
      });
    };
  },
  // if you need to add cell instance APIs...
  // createCell: <TData extends RowData>(cell, column, row, table): void => {},
  // if you need to add column instance APIs...
  // createColumn: <TData extends RowData>(column, table): void => {},
  // if you need to add header instance APIs...
  // createHeader: <TData extends RowData>(header, table): void => {},
};
//end of custom feature code
